/**
 * Audio Service for After5PM Games
 *
 * Provides procedural retro-style sound effects and background music
 * using the Web Audio API. No external audio files needed.
 */

type SoundEffect =
  | 'blip'
  | 'boop'
  | 'hit'
  | 'score'
  | 'lose'
  | 'win'
  | 'explosion'
  | 'laser'
  | 'powerup'
  | 'coin'
  | 'jump'
  | 'death'
  | 'levelup'
  | 'pause'
  | 'unpause'
  | 'menuSelect'
  | 'menuMove'
  | 'countdown';

interface AudioSettings {
  masterVolume: number;
  sfxVolume: number;
  musicVolume: number;
  sfxEnabled: boolean;
  musicEnabled: boolean;
}

const DEFAULT_SETTINGS: AudioSettings = {
  masterVolume: 0.7,
  sfxVolume: 0.8,
  musicVolume: 0.5,
  sfxEnabled: true,
  musicEnabled: true,
};

const STORAGE_KEY = 'after5pm-audio-settings';

class AudioService {
  private audioContext: AudioContext | null = null;
  private settings: AudioSettings = DEFAULT_SETTINGS;
  private musicOscillators: OscillatorNode[] = [];
  private musicGain: GainNode | null = null;
  private isMusicPlaying = false;

  constructor() {
    this.loadSettings();
  }

  /**
   * Initialize the audio context (must be called after user interaction)
   */
  init(): void {
    if (!this.audioContext) {
      this.audioContext = new (
        window.AudioContext ||
        (window as unknown as { webkitAudioContext: typeof AudioContext })
          .webkitAudioContext
      )();
    }
    if (this.audioContext.state === 'suspended') {
      this.audioContext.resume();
    }
  }

  /**
   * Load settings from localStorage
   */
  private loadSettings(): void {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        this.settings = { ...DEFAULT_SETTINGS, ...parsed };
      }
    } catch {
      this.settings = DEFAULT_SETTINGS;
    }
  }

  /**
   * Save settings to localStorage
   */
  private saveSettings(): void {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(this.settings));
    } catch {
      // Storage not available
    }
  }

  /**
   * Get current settings
   */
  getSettings(): AudioSettings {
    return { ...this.settings };
  }

  /**
   * Update settings
   */
  updateSettings(updates: Partial<AudioSettings>): void {
    this.settings = { ...this.settings, ...updates };
    this.saveSettings();

    // Update music volume if playing
    if (this.musicGain) {
      this.musicGain.gain.value = this.getMusicVolume();
    }
  }

  /**
   * Get effective SFX volume
   */
  private getSfxVolume(): number {
    if (!this.settings.sfxEnabled) return 0;
    return this.settings.masterVolume * this.settings.sfxVolume;
  }

  /**
   * Get effective music volume
   */
  private getMusicVolume(): number {
    if (!this.settings.musicEnabled) return 0;
    return this.settings.masterVolume * this.settings.musicVolume;
  }

  /**
   * Play a sound effect
   */
  play(sound: SoundEffect): void {
    if (!this.audioContext || !this.settings.sfxEnabled) return;
    this.init();

    const volume = this.getSfxVolume();
    if (volume === 0) return;

    switch (sound) {
      case 'blip':
        this.playTone(880, 0.05, 'square', volume);
        break;
      case 'boop':
        this.playTone(220, 0.08, 'square', volume);
        break;
      case 'hit':
        this.playTone(150, 0.1, 'sawtooth', volume, -200);
        break;
      case 'score':
        this.playArpeggio([523, 659, 784], 0.08, 'square', volume);
        break;
      case 'lose':
        this.playArpeggio([400, 300, 200, 100], 0.15, 'sawtooth', volume);
        break;
      case 'win':
        this.playArpeggio([523, 659, 784, 1047], 0.12, 'square', volume);
        break;
      case 'explosion':
        this.playNoise(0.3, volume);
        break;
      case 'laser':
        this.playTone(1200, 0.1, 'sawtooth', volume, -1000);
        break;
      case 'powerup':
        this.playArpeggio([262, 330, 392, 523, 659, 784], 0.06, 'sine', volume);
        break;
      case 'coin':
        this.playTone(988, 0.05, 'square', volume);
        setTimeout(() => this.playTone(1319, 0.1, 'square', volume), 50);
        break;
      case 'jump':
        this.playTone(200, 0.15, 'square', volume, 400);
        break;
      case 'death':
        this.playArpeggio(
          [440, 415, 392, 370, 349, 330],
          0.1,
          'square',
          volume
        );
        break;
      case 'levelup':
        this.playArpeggio(
          [523, 659, 784, 1047, 784, 659, 523],
          0.08,
          'square',
          volume
        );
        break;
      case 'pause':
        this.playTone(440, 0.05, 'sine', volume * 0.5);
        break;
      case 'unpause':
        this.playTone(880, 0.05, 'sine', volume * 0.5);
        break;
      case 'menuSelect':
        this.playTone(660, 0.08, 'square', volume);
        break;
      case 'menuMove':
        this.playTone(440, 0.03, 'square', volume * 0.5);
        break;
      case 'countdown':
        this.playTone(440, 0.1, 'sine', volume);
        break;
    }
  }

  /**
   * Play a single tone
   */
  private playTone(
    frequency: number,
    duration: number,
    type: OscillatorType,
    volume: number,
    frequencySlide = 0
  ): void {
    if (!this.audioContext) return;

    const osc = this.audioContext.createOscillator();
    const gain = this.audioContext.createGain();

    osc.type = type;
    osc.frequency.setValueAtTime(frequency, this.audioContext.currentTime);

    if (frequencySlide !== 0) {
      osc.frequency.linearRampToValueAtTime(
        frequency + frequencySlide,
        this.audioContext.currentTime + duration
      );
    }

    gain.gain.setValueAtTime(volume * 0.3, this.audioContext.currentTime);
    gain.gain.exponentialRampToValueAtTime(
      0.001,
      this.audioContext.currentTime + duration
    );

    osc.connect(gain);
    gain.connect(this.audioContext.destination);

    osc.start();
    osc.stop(this.audioContext.currentTime + duration);
  }

  /**
   * Play an arpeggio (sequence of tones)
   */
  private playArpeggio(
    frequencies: number[],
    noteDuration: number,
    type: OscillatorType,
    volume: number
  ): void {
    frequencies.forEach((freq, i) => {
      setTimeout(
        () => {
          this.playTone(freq, noteDuration, type, volume);
        },
        i * noteDuration * 1000
      );
    });
  }

  /**
   * Play noise (for explosions, etc.)
   */
  private playNoise(duration: number, volume: number): void {
    if (!this.audioContext) return;

    const bufferSize = this.audioContext.sampleRate * duration;
    const buffer = this.audioContext.createBuffer(
      1,
      bufferSize,
      this.audioContext.sampleRate
    );
    const data = buffer.getChannelData(0);

    for (let i = 0; i < bufferSize; i++) {
      data[i] = Math.random() * 2 - 1;
    }

    const noise = this.audioContext.createBufferSource();
    noise.buffer = buffer;

    const gain = this.audioContext.createGain();
    gain.gain.setValueAtTime(volume * 0.3, this.audioContext.currentTime);
    gain.gain.exponentialRampToValueAtTime(
      0.001,
      this.audioContext.currentTime + duration
    );

    // Add a low-pass filter for a more explosion-like sound
    const filter = this.audioContext.createBiquadFilter();
    filter.type = 'lowpass';
    filter.frequency.setValueAtTime(1000, this.audioContext.currentTime);
    filter.frequency.exponentialRampToValueAtTime(
      100,
      this.audioContext.currentTime + duration
    );

    noise.connect(filter);
    filter.connect(gain);
    gain.connect(this.audioContext.destination);

    noise.start();
    noise.stop(this.audioContext.currentTime + duration);
  }

  /**
   * Start background music (procedural chiptune)
   * Can use generic styles or game-specific tracks
   */
  startMusic(
    style:
      | 'arcade'
      | 'chill'
      | 'intense'
      | 'tennis'
      | 'wall-breaker'
      | 'invaders'
      | 'falling-blocks'
      | 'snake'
      | 'pill-muncher'
      | 'blasteroids'
      | 'frog-jump'
      | 'flapping-chick'
      | 'onslaught' = 'arcade'
  ): void {
    if (
      !this.audioContext ||
      !this.settings.musicEnabled ||
      this.isMusicPlaying
    )
      return;
    this.init();

    this.stopMusic();
    this.isMusicPlaying = true;

    this.musicGain = this.audioContext.createGain();
    this.musicGain.gain.value = this.getMusicVolume() * 0.15;
    this.musicGain.connect(this.audioContext.destination);

    const pattern = this.getMusicPattern(style);
    this.playMusicLoop(pattern);
  }

  /**
   * Get music pattern based on style or game
   * Each game has a unique musical theme
   */
  private getMusicPattern(style: string): {
    notes: number[];
    durations: number[];
    bpm: number;
    waveform?: OscillatorType;
  } {
    switch (style) {
      // === Game-specific tracks ===

      // Tennis: Simple, rhythmic, back-and-forth feel
      case 'tennis':
        return {
          notes: [330, 330, 392, 330, 294, 294, 262, 294],
          durations: [0.25, 0.25, 0.5, 0.25, 0.25, 0.25, 0.5, 0.25],
          bpm: 120,
          waveform: 'square',
        };

      // Wall Breaker: Upbeat, bouncy, energetic
      case 'wall-breaker':
        return {
          notes: [392, 440, 494, 523, 494, 440, 392, 330, 294, 330, 392, 330],
          durations: [
            0.25, 0.25, 0.25, 0.5, 0.25, 0.25, 0.25, 0.25, 0.25, 0.25, 0.5,
            0.25,
          ],
          bpm: 150,
          waveform: 'square',
        };

      // Invaders: Ominous, marching, tension-building
      case 'invaders':
        return {
          notes: [165, 165, 196, 165, 147, 147, 165, 196, 220, 196, 165, 147],
          durations: [
            0.5, 0.25, 0.25, 0.5, 0.5, 0.25, 0.25, 0.25, 0.25, 0.25, 0.25, 0.5,
          ],
          bpm: 100,
          waveform: 'sawtooth',
        };

      // Falling Blocks: Russian-inspired, iconic melody (Korobeiniki-esque)
      case 'falling-blocks':
        return {
          notes: [
            659, 494, 523, 587, 523, 494, 440, 440, 523, 659, 587, 523, 494,
            523, 587, 659,
          ],
          durations: [
            0.25, 0.125, 0.125, 0.25, 0.125, 0.125, 0.25, 0.125, 0.125, 0.25,
            0.125, 0.125, 0.375, 0.125, 0.25, 0.5,
          ],
          bpm: 140,
          waveform: 'square',
        };

      // Snake: Sneaky, slithering, hypnotic
      case 'snake':
        return {
          notes: [262, 294, 330, 294, 262, 247, 220, 247, 262, 330, 392, 330],
          durations: [
            0.25, 0.25, 0.5, 0.25, 0.25, 0.25, 0.5, 0.25, 0.25, 0.25, 0.5, 0.25,
          ],
          bpm: 110,
          waveform: 'triangle',
        };

      // Pill Muncher: Playful, chase-like, iconic wakka-wakka vibe
      case 'pill-muncher':
        return {
          notes: [
            523, 587, 659, 587, 523, 440, 392, 440, 523, 659, 784, 659, 523,
            440, 523, 587,
          ],
          durations: [
            0.125, 0.125, 0.25, 0.125, 0.125, 0.125, 0.25, 0.125, 0.125, 0.125,
            0.25, 0.125, 0.125, 0.125, 0.125, 0.25,
          ],
          bpm: 160,
          waveform: 'square',
        };

      // Blasteroids: Spacey, floating, minimal
      case 'blasteroids':
        return {
          notes: [147, 165, 196, 220, 196, 165, 147, 131],
          durations: [0.5, 0.5, 0.5, 1, 0.5, 0.5, 0.5, 1],
          bpm: 70,
          waveform: 'sine',
        };

      // Frog Jump: Hoppy, adventurous, crossing-the-road tension
      case 'frog-jump':
        return {
          notes: [330, 392, 440, 392, 330, 294, 330, 392, 494, 440, 392, 330],
          durations: [
            0.25, 0.25, 0.5, 0.25, 0.25, 0.25, 0.25, 0.25, 0.5, 0.25, 0.25, 0.5,
          ],
          bpm: 130,
          waveform: 'square',
        };

      // Flapping Chick: Light, airy, casual
      case 'flapping-chick':
        return {
          notes: [523, 587, 659, 784, 659, 587, 523, 494, 440, 494, 523, 587],
          durations: [
            0.25, 0.25, 0.25, 0.5, 0.25, 0.25, 0.25, 0.25, 0.25, 0.25, 0.25,
            0.5,
          ],
          bpm: 120,
          waveform: 'triangle',
        };

      // Onslaught: Intense, driving, adrenaline-pumping
      case 'onslaught':
        return {
          notes: [196, 196, 233, 262, 196, 196, 175, 165, 196, 262, 294, 262],
          durations: [
            0.125, 0.125, 0.125, 0.25, 0.125, 0.125, 0.125, 0.125, 0.125, 0.125,
            0.25, 0.25,
          ],
          bpm: 170,
          waveform: 'sawtooth',
        };

      // === Generic styles ===
      case 'arcade':
        return {
          notes: [262, 294, 330, 349, 392, 349, 330, 294],
          durations: [0.25, 0.25, 0.25, 0.25, 0.5, 0.25, 0.25, 0.5],
          bpm: 140,
          waveform: 'square',
        };
      case 'chill':
        return {
          notes: [262, 330, 392, 330, 294, 349, 392, 330],
          durations: [0.5, 0.5, 0.5, 0.5, 0.5, 0.5, 0.5, 0.5],
          bpm: 90,
          waveform: 'sine',
        };
      case 'intense':
        return {
          notes: [196, 196, 233, 196, 175, 196, 233, 262],
          durations: [0.125, 0.125, 0.25, 0.125, 0.125, 0.125, 0.125, 0.25],
          bpm: 160,
          waveform: 'sawtooth',
        };
      default:
        return {
          notes: [262, 294, 330, 349, 392, 349, 330, 294],
          durations: [0.25, 0.25, 0.25, 0.25, 0.5, 0.25, 0.25, 0.5],
          bpm: 140,
          waveform: 'square',
        };
    }
  }

  /**
   * Play music loop
   */
  private playMusicLoop(pattern: {
    notes: number[];
    durations: number[];
    bpm: number;
    waveform?: OscillatorType;
  }): void {
    if (!this.audioContext || !this.musicGain || !this.isMusicPlaying) return;

    const beatDuration = 60 / pattern.bpm;
    const waveform = pattern.waveform || 'square';
    let time = this.audioContext.currentTime;

    pattern.notes.forEach((note, i) => {
      if (!this.audioContext || !this.musicGain) return;

      const osc = this.audioContext.createOscillator();
      const noteGain = this.audioContext.createGain();

      osc.type = waveform;
      osc.frequency.value = note;

      const duration = pattern.durations[i] * beatDuration;
      noteGain.gain.setValueAtTime(0.3, time);
      noteGain.gain.setValueAtTime(0.3, time + duration * 0.8);
      noteGain.gain.exponentialRampToValueAtTime(0.001, time + duration);

      osc.connect(noteGain);
      noteGain.connect(this.musicGain);

      osc.start(time);
      osc.stop(time + duration);

      this.musicOscillators.push(osc);
      time += duration;
    });

    // Schedule next loop
    const loopDuration =
      pattern.durations.reduce((a, b) => a + b, 0) * beatDuration;
    setTimeout(() => {
      if (this.isMusicPlaying) {
        this.playMusicLoop(pattern);
      }
    }, loopDuration * 1000);
  }

  /**
   * Stop background music
   */
  stopMusic(): void {
    this.isMusicPlaying = false;
    this.musicOscillators.forEach((osc) => {
      try {
        osc.stop();
      } catch {
        // Already stopped
      }
    });
    this.musicOscillators = [];
    this.musicGain = null;
  }

  /**
   * Toggle SFX
   */
  toggleSfx(): boolean {
    this.settings.sfxEnabled = !this.settings.sfxEnabled;
    this.saveSettings();
    return this.settings.sfxEnabled;
  }

  /**
   * Toggle music
   */
  toggleMusic(): boolean {
    this.settings.musicEnabled = !this.settings.musicEnabled;
    this.saveSettings();

    if (!this.settings.musicEnabled) {
      this.stopMusic();
    }

    return this.settings.musicEnabled;
  }

  /**
   * Check if music is currently playing
   */
  isMusicCurrentlyPlaying(): boolean {
    return this.isMusicPlaying;
  }
}

// Singleton instance
export const audioService = new AudioService();

// Export types
export type { SoundEffect, AudioSettings };
